import imageCompression from 'browser-image-compression';

// Compression options
const COMPRESSION_OPTIONS = {
  maxSizeMB: 0.5, // Target 500KB max
  maxWidthOrHeight: 1920, // Max dimension
  useWebWorker: true,
  fileType: 'image/jpeg' as const,
  initialQuality: 0.8,
};

// Maximum file size before compression is required (5MB)
export const MAX_FILE_SIZE = 5 * 1024 * 1024;

// Allowed MIME types
export const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Validate that a file is an allowed image type
 */
export function isValidImageType(file: File): boolean {
  return ALLOWED_MIME_TYPES.includes(file.type);
}

/**
 * Validate file size is within limits
 */
export function isValidFileSize(file: File): boolean {
  return file.size <= MAX_FILE_SIZE;
}

/**
 * Compress an image file for upload
 * - Targets 500KB max file size
 * - Resizes to max 1920px on longest side
 * - Converts to JPEG for consistent handling
 */
export async function compressImage(file: File): Promise<File> {
  // Skip compression if file is already small enough
  if (file.size <= COMPRESSION_OPTIONS.maxSizeMB * 1024 * 1024) {
    // Still convert to JPEG for consistency if needed
    if (file.type === 'image/jpeg') {
      return file;
    }
  }

  try {
    const compressedFile = await imageCompression(file, COMPRESSION_OPTIONS);

    // Ensure the file has the correct extension
    const fileName = file.name.replace(/\.[^/.]+$/, '.jpg');

    return new File([compressedFile], fileName, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch (error) {
    console.error('Image compression failed:', error);
    // Return original file if compression fails
    return file;
  }
}

/**
 * Get a preview URL for an image file
 * Returns a data URL that can be used as an img src
 */
export function getImagePreviewUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
